import React, { useMemo } from "react";
import call, { type Output } from "./call";
import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import { useChannelUpdates } from "./ws/useChannelUpdates";

type RawMetrologyOverrides = Output<
  "GET",
  "/projects/:projectId(uuid)/quantities/metrology-overrides"
>["body"];

export type MetrologyOverrides = {
  [date: number]: {
    [quantityName: string]: boolean;
  };
};

const cache = new WeakMap<RawMetrologyOverrides, MetrologyOverrides>();

export const useMetrologyOverrides = (projectId: string) => {
  const rawMetrologyOverrides = useQuery(
    "/projects/:projectId(uuid)/quantities/metrology-overrides",
    {
      routeParams: { projectId },
    },
  ).body;

  const getCachedData = useGetQueryData(
    "/projects/:projectId(uuid)/quantities/metrology-overrides",
    {
      routeParams: { projectId },
    },
  );

  const readItems = React.useCallback(() => getCachedData()?.body, [getCachedData]);
  const fetchUpdatedItems = React.useCallback(
    async (since: string) => {
      const { manualValidations, deletedManualValidationIds } = (
        await call("GET", "/projects/:projectId(uuid)/quantities/metrology-overrides/updates", {
          routeParams: { projectId },
          query: { since: new Date(since) },
        })
      ).body;
      return [manualValidations, deletedManualValidationIds] as const;
    },
    [projectId],
  );
  const mutateItems = useMutateBody("/projects/:projectId(uuid)/quantities/metrology-overrides", {
    routeParams: { projectId },
  });

  useChannelUpdates(
    { key: "metrology_overrides", projectId },
    {
      fetchUpdatedItems,
      mutateItems,
      readItems,
    },
  );

  return useMemo(() => {
    const cached = cache.get(rawMetrologyOverrides);
    if (cached) return cached;
    const result = rawMetrologyOverrides.reduce<MetrologyOverrides>((acc, override) => {
      const { analysisDate, quantityName, validity } = override;
      if (!acc[analysisDate]) acc[analysisDate] = {};
      acc[analysisDate][quantityName] = validity;
      return acc;
    }, {});

    cache.set(rawMetrologyOverrides, result);
    return result;
  }, [rawMetrologyOverrides]);
};
